import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  Alert,
  Spinner,
  Box,
  ColumnLayout,
  CopyToClipboard,
} from "@cloudscape-design/components";
import { SearchForm, CustomerDataPanel, WorkflowPanel, ScheduledEventsPanel, StatusBox, RecentSearchesRow } from "../Components";
import { ApiClient, WorkflowStatus } from "../services";
import { Realm, Stage, TABLE_NAMES, LABELS, NotifyFn } from "../constants";
import { HashParams, ResultFormatter, parseRealm } from "../utils";
import { RecentSearchEntry } from "../utils/RecentSearches";
import { useCustomerSearch } from "../hooks";

interface SearchPageProps {
  onNotify: NotifyFn;
}

const api = ApiClient.getInstance();

const getUrlParams = (): { customerId: string; realm: Realm } => {
  const params = HashParams.parse();
  return {
    customerId: params.get("customerId") || "",
    realm: parseRealm(params.get("realm")),
  };
};

export const SearchPage: React.FC<SearchPageProps> = ({ onNotify }) => {
  const urlParams = useMemo(getUrlParams, []);
  const [customerId, setCustomerId] = useState(urlParams.customerId);
  const [realm, setRealm] = useState<Realm>(urlParams.realm);
  const [stage, setStage] = useState<Stage>("beta");
  const [includeHistory, setIncludeHistory] = useState(false);
  const initialSearchDone = useRef(false);

  const {
    loading,
    searched,
    searchedId,
    searchedRealm,
    data,
    history,
    workflows,
    scheduledEvents,
    errors,
    hasErrors,
    recentSearches,
    performSearch,
    handleRedrive,
    clearRecentSearches,
  } = useCustomerSearch({ onNotify });

  useEffect(() => {
    api.init().then(() => setStage(api.getStage()));
  }, []);

  const handleSearch = useCallback((): Promise<void> => performSearch(customerId, realm, includeHistory), [
    performSearch,
    customerId,
    realm,
    includeHistory,
  ]);

  const handleRecentSelect = useCallback(
    (entry: RecentSearchEntry): void => {
      setCustomerId(entry.customerId);
      setRealm(entry.realm);
      performSearch(entry.customerId, entry.realm, includeHistory);
    },
    [performSearch, includeHistory]
  );

  useEffect(() => {
    if (!initialSearchDone.current && urlParams.customerId) {
      initialSearchDone.current = true;
      handleSearch();
    }
  }, [urlParams.customerId, handleSearch]);

  const hasData = data && Object.values(data).some((v) => v !== null);

  const stats = useMemo(
    () => ({
      tablesFound: data ? TABLE_NAMES.filter((t) => data[t] !== null).length : 0,
      historyVersions: history ? TABLE_NAMES.reduce((sum, t) => sum + (history[t]?.length || 0), 0) : 0,
      workflowsTotal: workflows.length,
      workflowsFailed: workflows.filter((w) => w.status === WorkflowStatus.FAILED).length,
    }),
    [data, history, workflows]
  );

  const copyText = useMemo(
    () =>
      ResultFormatter.formatSearchResults({
        customerId: searchedId,
        realm: searchedRealm,
        data,
        history,
        workflows,
        scheduledEvents: scheduledEvents?.schedules || [],
      }),
    [searchedId, searchedRealm, data, history, workflows, scheduledEvents]
  );

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={LABELS.SEARCH_DESCRIPTION}>
          {LABELS.CUSTOMER_SEARCH}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container>
          <SpaceBetween size="m">
            <SearchForm
              customerId={customerId}
              realm={realm}
              stage={stage}
              includeHistory={includeHistory}
              loading={loading}
              onCustomerIdChange={setCustomerId}
              onRealmChange={setRealm}
              onHistoryChange={setIncludeHistory}
              onSearch={handleSearch}
            />
            <RecentSearchesRow entries={recentSearches} onSelect={handleRecentSelect} onClear={clearRecentSearches} />
          </SpaceBetween>
        </Container>

        {loading && (
          <Container>
            <Box textAlign="center" padding="xxl">
              <SpaceBetween size="m" alignItems="center">
                <Spinner size="large" />
                <Box variant="p" color="text-status-inactive">
                  {LABELS.SEARCHING}
                </Box>
              </SpaceBetween>
            </Box>
          </Container>
        )}

        {!loading && hasErrors && (
          <SpaceBetween size="s">
            {errors.customerData && (
              <Alert type="error" header={LABELS.CUSTOMER_DATA_ERROR}>
                {errors.customerData}
              </Alert>
            )}
            {errors.workflows && (
              <Alert type="error" header={LABELS.WORKFLOWS_ERROR}>
                {errors.workflows}
              </Alert>
            )}
            {errors.scheduledEvents && (
              <Alert type="error" header={LABELS.SCHEDULED_EVENTS_ERROR}>
                {errors.scheduledEvents}
              </Alert>
            )}
          </SpaceBetween>
        )}

        {!loading && searched && !hasErrors && !hasData && workflows.length === 0 && (
          <Alert type="info" header={LABELS.NO_RESULTS_FOUND}>
            No data found for customer ID: <strong>{searchedId}</strong> in realm <strong>{searchedRealm}</strong>
          </Alert>
        )}

        {!loading && searched && (hasData || workflows.length > 0) && (
          <Container
            header={
              <Header
                variant="h2"
                description={`${LABELS.RESULTS_FOR} ${searchedId}`}
                actions={
                  <CopyToClipboard
                    copyButtonText={LABELS.COPY_RESULTS}
                    copySuccessText={LABELS.COPIED}
                    copyErrorText={LABELS.ERROR}
                    textToCopy={copyText}
                  />
                }
              >
                {LABELS.SEARCH_RESULTS}
              </Header>
            }
          >
            <ColumnLayout columns={4} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">{LABELS.TABLES_FOUND}</Box>
                <Box fontSize="display-l" fontWeight="bold">
                  {stats.tablesFound}
                  <Box variant="small" display="inline" color="text-status-inactive">
                    {" "}
                    / {TABLE_NAMES.length}
                  </Box>
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{LABELS.HISTORY_VERSIONS}</Box>
                <Box fontSize="display-l" fontWeight="bold">
                  {stats.historyVersions}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{LABELS.WORKFLOWS}</Box>
                <Box fontSize="display-l" fontWeight="bold">
                  {stats.workflowsTotal}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{LABELS.FAILED_WORKFLOWS}</Box>
                <Box fontSize="display-l" fontWeight="bold" color={stats.workflowsFailed > 0 ? "text-status-error" : undefined}>
                  {stats.workflowsFailed}
                </Box>
              </div>
            </ColumnLayout>
          </Container>
        )}

        {!loading && hasData && data && (
          <>
            <StatusBox data={data} />
            <CustomerDataPanel data={data} history={history} />
          </>
        )}

        {!loading && workflows.length > 0 && <WorkflowPanel workflows={workflows} loading={false} onRedrive={handleRedrive} />}

        {!loading && scheduledEvents && scheduledEvents.schedules.length > 0 && (
          <ScheduledEventsPanel schedules={scheduledEvents.schedules} />
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};
